/**
 * Parse the vendor API error response and split it into per-field errors
 * (for inline form rendering) plus a single summary string (for the toast).
 *
 * DRF typically returns one of:
 *   { errors: { fieldname: ["msg"], ... } }
 *   { fieldname: ["msg"], ... }               (top-level)
 *   { non_field_errors: ["msg"] } / { detail: "msg" }
 *   { error: "msg" }                          (vendor endpoint convention)
 *   a bare string (fallback)
 *
 * The previous handler dumped the first error onto `fieldErrors.name`, which
 * made every server rejection look like a Name problem.
 */
export const parseVendorApiErrors = (
  responseData: unknown,
): { fieldErrors: Record<string, string>; summary: string } => {
  const fallback = 'Failed to save source';
  if (responseData == null) return { fieldErrors: {}, summary: fallback };
  if (typeof responseData === 'string') {
    return { fieldErrors: {}, summary: responseData || fallback };
  }
  if (typeof responseData !== 'object') {
    return { fieldErrors: {}, summary: fallback };
  }

  const data = responseData as Record<string, unknown>;
  const errorsContainer =
    data['errors'] && typeof data['errors'] === 'object' && !Array.isArray(data['errors'])
      ? (data['errors'] as Record<string, unknown>)
      : data;

  const fieldErrors: Record<string, string> = {};
  let firstMessage: string | undefined;

  for (const [key, value] of Object.entries(errorsContainer)) {
    const msg = Array.isArray(value)
      ? String(value[0] ?? '')
      : typeof value === 'string'
        ? value
        : typeof value === 'object' && value !== null
          ? String(Object.values(value as Record<string, unknown>)[0] ?? '')
          : '';
    if (!msg) continue;
    firstMessage ??= msg;
    if (key === 'non_field_errors' || key === 'detail' || key === 'error') continue;
    fieldErrors[key] = msg;
  }

  const summary =
    firstMessage ??
    (typeof data['error'] === 'string' ? data['error'] : undefined) ??
    (typeof data['detail'] === 'string' ? data['detail'] : undefined) ??
    fallback;

  return { fieldErrors, summary };
};
